(() => {
  const focusable = 'a[href], area[href], button, input, select, textarea, summary, [tabindex], [contenteditable="true"], audio[controls], video[controls]';

  function init(scope = document) {
    const roots = [...scope.querySelectorAll('[data-pd3-hero]')];
    if (scope.matches?.('[data-pd3-hero]')) roots.unshift(scope);
    roots.forEach(root => {
      if (root.dataset.ready) return;
      const track = root.querySelector('.pd3-campaigns');
      const panels = [...root.querySelectorAll('[data-pd3-campaign]')];
      const bar = root.querySelector('[role="tablist"]');
      const tabs = bar ? [...bar.querySelectorAll('[role="tab"]')] : [];
      if (!track || !panels.length || panels.length !== tabs.length) return;

      const previous = root.querySelector('[data-pd3-prev]');
      const next = root.querySelector('[data-pd3-next]');
      const rotation = root.querySelector('[data-pd3-rotation]');
      const rotationLabel = rotation?.querySelector('[data-pd3-rotation-label]');
      const status = root.querySelector('[data-pd3-status]');
      const originalTabindex = new Map();
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const autoplay = root.dataset.pd3Autoplay === 'true' && panels.length > 1;
      let userPaused = reducedMotion.matches || Boolean(window.Shopify?.designMode);
      let hovering = window.matchMedia('(hover: hover)').matches && root.matches(':hover');
      let focusBlocked = root.contains(document.activeElement);
      let visible = !('IntersectionObserver' in window);
      let destroyed = false;
      let active = -1;
      let scrollFrame = 0;
      let resizeFrame = 0;
      let rotationTimer = 0;
      let width = track.clientWidth;
      let sizeObserver;
      let visibilityObserver;

      const wrap = index => (index + panels.length) % panels.length;
      const panelLeft = index => track.scrollLeft + panels[index].getBoundingClientRect().left - track.getBoundingClientRect().left - track.clientLeft;

      function updateRotation() {
        window.clearTimeout(rotationTimer);
        rotationTimer = 0;
        if (rotation) {
          rotation.dataset.paused = String(userPaused);
          rotation.setAttribute('aria-label', userPaused ? rotation.dataset.playLabel : rotation.dataset.pauseLabel);
          rotation.setAttribute('title', userPaused ? rotation.dataset.playLabel : rotation.dataset.pauseLabel);
          if (rotationLabel) rotationLabel.textContent = userPaused ? rotation.dataset.playText : rotation.dataset.pauseText;
        }
        if (status) status.setAttribute('aria-live', userPaused || !autoplay ? 'polite' : 'off');
        if (!autoplay || userPaused || hovering || focusBlocked || !visible || document.hidden || destroyed) return;
        rotationTimer = window.setTimeout(() => {
          rotationTimer = 0;
          select(active + 1);
          updateRotation();
        }, 8000);
      }

      function pauseManually() {
        userPaused = true;
        updateRotation();
      }

      function activate(index, announce = true) {
        if (index === active) return;
        const previousActive = active;
        active = index;
        const focusedPanel = panels.findIndex(panel => panel.contains(document.activeElement));
        if (focusedPanel !== -1 && focusedPanel !== index) tabs[index].focus({ preventScroll: true });
        panels.forEach((panel, i) => {
          const selected = i === index;
          panel.inert = !selected;
          panel.setAttribute('aria-hidden', String(!selected));
          panel.querySelectorAll(focusable).forEach(element => {
            if (!selected) {
              if (!originalTabindex.has(element)) originalTabindex.set(element, element.getAttribute('tabindex'));
              element.setAttribute('tabindex', '-1');
            } else if (originalTabindex.has(element)) {
              const value = originalTabindex.get(element);
              if (value === null) element.removeAttribute('tabindex');
              else element.setAttribute('tabindex', value);
              originalTabindex.delete(element);
            }
          });
          tabs[i].setAttribute('aria-selected', String(selected));
          tabs[i].tabIndex = selected ? 0 : -1;
        });
        if (status && announce && previousActive !== -1 && (userPaused || !autoplay)) status.textContent = tabs[index].textContent.trim();
      }

      function sync() {
        scrollFrame = 0;
        const edge = track.getBoundingClientRect().left + track.clientLeft;
        let nearest = 0;
        let distance = Infinity;
        panels.forEach((panel, index) => {
          const current = Math.abs(panel.getBoundingClientRect().left - edge);
          if (current < distance) {
            nearest = index;
            distance = current;
          }
        });
        activate(nearest);
      }

      function select(index, { focus = false, instant = false } = {}) {
        index = wrap(index);
        if (focus) tabs[index].focus({ preventScroll: true });
        track.scrollTo({ left: panelLeft(index), behavior: instant || reducedMotion.matches ? 'auto' : 'smooth' });
        if (instant || reducedMotion.matches) activate(index);
      }

      panels.forEach((panel, i) => {
        panel.hidden = false;
        panel.setAttribute('role', 'tabpanel');
        if (tabs[i].id) panel.setAttribute('aria-labelledby', tabs[i].id);
        if (panel.id) tabs[i].setAttribute('aria-controls', panel.id);
      });
      tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => {
          pauseManually();
          select(i);
        });
        tab.addEventListener('keydown', event => {
          let index;
          if (event.key === 'ArrowRight') index = i + 1;
          else if (event.key === 'ArrowLeft') index = i - 1;
          else if (event.key === 'Home') index = 0;
          else if (event.key === 'End') index = panels.length - 1;
          else return;
          event.preventDefault();
          pauseManually();
          select(index, { focus: true });
        });
      });
      previous?.addEventListener('click', () => {
        pauseManually();
        select(active - 1);
      });
      next?.addEventListener('click', () => {
        pauseManually();
        select(active + 1);
      });
      rotation?.addEventListener('click', () => {
        userPaused = !userPaused;
        if (!userPaused) focusBlocked = false;
        updateRotation();
      });
      track.addEventListener('pointerdown', pauseManually, { passive: true });
      track.addEventListener('wheel', event => {
        if (Math.abs(event.deltaX) > 0 || event.shiftKey) pauseManually();
      }, { passive: true });
      track.addEventListener('scroll', () => {
        if (!scrollFrame) scrollFrame = requestAnimationFrame(sync);
      }, { passive: true });
      track.addEventListener('scrollend', sync);
      root.addEventListener('pointerenter', event => {
        if (event.pointerType === 'touch') return;
        hovering = true;
        updateRotation();
      });
      root.addEventListener('pointerleave', event => {
        if (event.pointerType === 'touch') return;
        hovering = false;
        updateRotation();
      });
      root.addEventListener('focusin', event => {
        focusBlocked = true;
        if (event.target !== rotation) userPaused = true;
        updateRotation();
      });
      root.addEventListener('focusout', event => {
        if (root.contains(event.relatedTarget)) return;
        focusBlocked = false;
        updateRotation();
      });

      function resized() {
        if (width === track.clientWidth) return;
        width = track.clientWidth;
        cancelAnimationFrame(resizeFrame);
        resizeFrame = requestAnimationFrame(() => select(active, { instant: true }));
      }
      if ('ResizeObserver' in window) {
        sizeObserver = new ResizeObserver(resized);
        sizeObserver.observe(track);
      } else window.addEventListener('resize', resized, { passive: true });
      if ('IntersectionObserver' in window) {
        visibilityObserver = new IntersectionObserver(entries => {
          visible = entries.some(entry => entry.target === track && entry.isIntersecting && entry.intersectionRatio >= 0.25);
          updateRotation();
        }, { threshold: [0, 0.25] });
        visibilityObserver.observe(track);
      }
      function motionChanged() {
        if (reducedMotion.matches) userPaused = true;
        updateRotation();
      }
      reducedMotion.addEventListener('change', motionChanged);
      document.addEventListener('visibilitychange', updateRotation);
      root.addEventListener('shopify:block:select', event => {
        const index = panels.findIndex(panel => panel === event.target || panel.contains(event.target));
        if (index !== -1) {
          pauseManually();
          select(index, { instant: true });
        }
      });
      function unload(event) {
        if (event.target !== root && !event.target.contains(root)) return;
        destroyed = true;
        window.clearTimeout(rotationTimer);
        sizeObserver?.disconnect();
        visibilityObserver?.disconnect();
        window.removeEventListener('resize', resized);
        reducedMotion.removeEventListener('change', motionChanged);
        document.removeEventListener('visibilitychange', updateRotation);
        cancelAnimationFrame(scrollFrame);
        cancelAnimationFrame(resizeFrame);
        document.removeEventListener('shopify:section:unload', unload);
      }
      document.addEventListener('shopify:section:unload', unload);

      root.dataset.ready = 'true';
      root.querySelector('[data-pd3-controls]')?.removeAttribute('hidden');
      bar.hidden = panels.length < 2;
      if (previous) previous.hidden = panels.length < 2;
      if (next) next.hidden = panels.length < 2;
      if (rotation) rotation.hidden = !autoplay;
      activate(0, false);
      sync();
      updateRotation();
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
